import { useEffect, useState } from 'react'

import { Link, useLocation, useNavigate } from 'react-router-dom'

import { theme } from '@/config/theme'

const NAV_LINKS = [
  { to: '/treehouses', label: 'Treehouses & Cabins' },
  { to: '/#experiences', label: 'Experiences' },
  { to: '/restaurant', label: 'Restaurant' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact Us' },
]

const WA_URL = `https://wa.me/919418040013?text=${encodeURIComponent('Hi, I would like to enquire about a stay at Scenic Solitude.')}`

export function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isHome = pathname === '/'
  const transparent = isHome && !scrolled

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])

  function scrollTo(e: React.MouseEvent<HTMLAnchorElement>, to: string) {
    if (to.startsWith('/#')) {
      e.preventDefault()
      setMenuOpen(false)
      const id = to.slice(2)
      if (isHome) {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
      } else {
        navigate('/')
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 400)
      }
    } else {
      setMenuOpen(false)
    }
  }

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-700 ${transparent
          ? 'bg-gradient-to-b from-amber-600/30 via-amber-500/10 to-transparent'
          : theme.surfaceDark
        }`}
    >
      {/* Row 1 — Logo centred */}
      <div className="flex justify-center pt-4 pb-2">
        <Link to="/" className="flex flex-col items-center gap-1.5">
          <img
            src="/images/logo3.jpeg"
            alt="Scenic Solitude"
            className="h-10 w-auto object-contain"
          />
          <span className={`font-sans text-[15px] tracking-[0.22em] font-semibold uppercase leading-tight transition-colors duration-500 ${theme.navScrolledText}`}>
            Scenic Solitude
          </span>
          <span className={`text-[8px] tracking-[0.45em] uppercase font-normal transition-colors duration-500 ${theme.textDarkSubtitle}`}>
            Himalayas
          </span>
        </Link>
      </div>

      {/* Row 2 — Nav links centred, desktop only */}
      <div className={`hidden lg:block border-t transition-colors duration-700 ${theme.navScrolledBorder}`}>
        <nav className="flex items-center justify-center gap-0">
          {NAV_LINKS.map(({ to, label }, i) => (
            <a
              key={to}
              href={to}
              onClick={(e) => scrollTo(e, to)}
              className={`relative px-6 py-3 text-[11px] tracking-widest2 uppercase font-medium transition-opacity duration-300 hover:opacity-60 ${theme.navScrolledText} ${i < NAV_LINKS.length - 1
                ? `after:absolute after:right-0 after:top-1/2 after:-translate-y-1/2 after:h-3 after:w-px ${theme.navLinkDivider}`
                : ''
                }`}
            >
              {label}
            </a>
          ))}
          {/* Reserve — inline with nav */}
          <a
            href={WA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`ml-4 border px-5 py-1.5 text-[11px] tracking-widest2 uppercase transition-all duration-300 ${theme.navScrolledReserve}`}
          >
            Reserve
          </a>
        </nav>
      </div>

      {/* Mobile row — hamburger */}
      <div className="lg:hidden flex items-center justify-end px-6 pb-3">
        <button
          className={theme.navScrolledHamburger}
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Menu"
        >
          <div className="flex flex-col gap-[5px] w-6">
            <span className={`block h-px bg-current transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
            <span className={`block h-px bg-current transition-opacity duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-px bg-current transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
          </div>
        </button>
      </div>

      {/* Mobile drawer — always dark ink bg for contrast regardless of theme */}
      <div className={`lg:hidden overflow-hidden transition-all duration-500 ${menuOpen ? 'max-h-screen' : 'max-h-0'}`}>
        <div className="bg-ink border-t border-cream-300/10 px-8 py-8 flex flex-col gap-5">
          {NAV_LINKS.map(({ to, label }) => (
            <a key={to} href={to} onClick={(e) => scrollTo(e, to)}
              className="text-[11px] tracking-widest2 uppercase font-medium text-cream-300 hover:opacity-60">
              {label}
            </a>
          ))}
          <a
            href={WA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 text-center border border-cream-300/30 px-6 py-3 text-[11px] tracking-widest2 uppercase font-medium text-cream-300"
          >
            Reserve · WhatsApp
          </a>
        </div>
      </div>
    </header>
  )
}
